import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, InputV2, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { CAR_PRICE_RANGE } from '../../CarRentalConsole.constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { FilterFacet } from '@apps/stays/components/filters/FilterFacet';
import { StyledSlider } from '@apps/stays/components/filters/StyledSlider';
import { formatCarPrice, getCarCurrencySymbol } from '../../CarRentalConsole.utils';

type PriceRangeFacetProps = Readonly<{
  currencyCode: string;
  dynamicPriceMax: number;
  navigatorLanguage: string;
  priceRange: readonly [number, number];
  onPriceRangeChange: (range: readonly [number, number]) => void;
}>;

export const PriceRangeFacet = ({ priceRange, onPriceRangeChange, dynamicPriceMax, currencyCode, navigatorLanguage }: PriceRangeFacetProps) => {
  const { tq } = useTranslation('carRentalConsole');
  const currencySymbol = getCarCurrencySymbol(currencyCode, navigatorLanguage);
  const description = tq`${formatCarPrice(CAR_PRICE_RANGE.MIN, currencyCode, navigatorLanguage)} → ${formatCarPrice(dynamicPriceMax, currencyCode, navigatorLanguage)}+`;

  const [isMinCleared, setIsMinCleared] = React.useState(false);
  const [isMaxCleared, setIsMaxCleared] = React.useState(false);
  const [minInput, setMinInput] = React.useState(priceRange[0].toString());
  const [maxInput, setMaxInput] = React.useState(priceRange[1].toString());

  const handleChange = useEventCallback((value: number | readonly number[]) => {
    const range: readonly [number, number] = Array.isArray(value) ? [value[0], value[1]] : [value, value];
    onPriceRangeChange(range);
  });

  const handleMinChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMinInput(value);
    setIsMinCleared(value === '');

    const numericValue = value === '' ? CAR_PRICE_RANGE.MIN : parseInt(value, 10);
    if (value === '' || !Number.isNaN(numericValue)) {
      handleChange([numericValue, priceRange[1]]);
    }
  });

  const handleMaxChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMaxInput(value);
    setIsMaxCleared(value === '');

    const numericValue = value === '' ? dynamicPriceMax : parseInt(value, 10);
    if (value === '' || !Number.isNaN(numericValue)) {
      handleChange([priceRange[0], numericValue]);
    }
  });

  const handleBlur = useEventCallback(() => {
    if (priceRange[0] > priceRange[1]) {
      onPriceRangeChange([priceRange[1], priceRange[0]]);
    }
  });

  React.useEffect(() => {
    if (!isMinCleared || (isMinCleared && priceRange[0] !== CAR_PRICE_RANGE.MIN)) {
      setMinInput(priceRange[0].toString());
      setIsMinCleared(false);
    }

    if (!isMaxCleared || (isMaxCleared && priceRange[1] !== dynamicPriceMax)) {
      setMaxInput(priceRange[1].toString());
      setIsMaxCleared(false);
    }
  }, [dynamicPriceMax, isMaxCleared, isMinCleared, priceRange]);

  return (
    <FilterFacet
      title={tq`Price (total)`}
      description={description}
    >
      <Flex
        gap={3}
        marginTop={5}
        alignItems="center"
      >
        <Box>
          <TextV2
            marginBottom={3}
            typographyVariant="label3"
          >
            {tq`Min`}
          </TextV2>
          <InputV2
            min={0}
            type="number"
            onBlur={handleBlur}
            inputMode="numeric"
            value={minInput}
            startElement={<TextV2 typographyVariant="body2">{currencySymbol}</TextV2>}
            onChange={handleMinChange}
          />
        </Box>
        <TextV2
          aria-hidden="true"
          marginTop={pxToRem(24)}
        >
          -
        </TextV2>
        <Box>
          <TextV2
            marginBottom={3}
            typographyVariant="label3"
          >
            {tq`Max`}
          </TextV2>
          <InputV2
            min={0}
            type="number"
            onBlur={handleBlur}
            inputMode="numeric"
            value={maxInput}
            startElement={<TextV2 typographyVariant="body2">{currencySymbol}</TextV2>}
            onChange={handleMaxChange}
          />
        </Box>
      </Flex>
      <Box marginTop={pxToRem(24)}>
        <StyledSlider
          className="slider"
          value={priceRange}
          max={dynamicPriceMax}
          trackClassName="track"
          thumbClassName="thumb"
          onChange={handleChange}
          min={CAR_PRICE_RANGE.MIN}
        />
      </Box>
    </FilterFacet>
  );
};
